/**
 * ServiceNeedCard
 *
 * Card component for capturing per-ServiceRequest details during enrollment:
 * service type, modality, focus conditions, and additional preferences.
 * @agentic connections
 */

import {
  Paper,
  Group,
  Stack,
  Text,
  Select,
  MultiSelect,
  Textarea,
  ActionIcon,
  SimpleGrid,
} from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import {
  REFERRAL_MODE_OPTIONS,
  SERVICE_CATEGORY_OPTIONS,
  SERVICE_TYPE_BY_CATEGORY,
  SERVICE_TYPE_OPTIONS,
  SERVICE_SPECIALTY_TREATMENT_CONDITION_OPTIONS,
} from "@benchmark/core/constants";
import type { ServiceNeedFormItem } from "@benchmark/core/types";

/**
 * Props for ServiceNeedCard.
 * @agentic connections
 */
export type ServiceNeedCardProps = {
  index: number;
  item: ServiceNeedFormItem;
  onChange: (index: number, updated: ServiceNeedFormItem) => void;
  onRemove: (index: number) => void;
  usedServiceTypes: string[];
  errors?: Record<string, string>;
};

const GROUPED_SERVICE_TYPE_OPTIONS = (SERVICE_CATEGORY_OPTIONS ?? []).map(
  (category) => ({
    group: category.label,
    items: (SERVICE_TYPE_BY_CATEGORY?.[category.value] ?? [])
      .map((typeValue) => {
        const serviceType = SERVICE_TYPE_OPTIONS?.find(
          (t) => t.value === typeValue,
        );
        return serviceType
          ? { value: serviceType.value, label: serviceType.label }
          : null;
      })
      .filter(Boolean) as { value: string; label: string }[],
  }),
);

const CONDITION_OPTIONS = SERVICE_SPECIALTY_TREATMENT_CONDITION_OPTIONS.map(
  (o) => ({
    value: o.value,
    label: o.label,
  }),
);

/**
 * Card for capturing details of a single service need.
 * @agentic connections
 */
export const ServiceNeedCard = ({
  index,
  item,
  onChange,
  onRemove,
  usedServiceTypes,
  errors,
}: ServiceNeedCardProps) => {
  const filteredServiceTypeOptions = GROUPED_SERVICE_TYPE_OPTIONS.map(
    (group) => ({
      ...group,
      items: group.items.filter(
        (opt) =>
          opt.value === item.serviceType ||
          !usedServiceTypes.includes(opt.value),
      ),
    }),
  ).filter((group) => group.items.length > 0);

  const serviceTypeLabel =
    SERVICE_TYPE_OPTIONS.find((o) => o.value === item.serviceType)?.label ||
    item.serviceType;

  const handleFieldChange = <K extends keyof ServiceNeedFormItem>(
    field: K,
    value: ServiceNeedFormItem[K],
  ) => {
    onChange(index, { ...item, [field]: value });
  };

  return (
    <Paper p="md" withBorder>
      <Stack gap="sm">
        <Group justify="space-between">
          <Text size="sm" fw={600}>
            {index + 1}.{" "}
            {item.serviceType ? serviceTypeLabel : "New Service Need"}
          </Text>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            onClick={() => onRemove(index)}
            aria-label="Remove service need"
          >
            <IconX size={14} />
          </ActionIcon>
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
          <Select
            label="Service Type"
            placeholder="Select service..."
            data={filteredServiceTypeOptions}
            value={item.serviceType || null}
            onChange={(value) => handleFieldChange("serviceType", value || "")}
            searchable
            withAsterisk
            error={errors?.serviceType}
          />

          <MultiSelect
            label="Mode"
            placeholder={
              item.modes.length === 0 ? "Select mode preferences..." : undefined
            }
            data={REFERRAL_MODE_OPTIONS}
            value={item.modes}
            onChange={(value) => handleFieldChange("modes", value)}
            searchable={false}
            clearable
            withAsterisk
            error={errors?.modes}
          />

          <MultiSelect
            label="Conditions"
            placeholder={
              item.conditions.length === 0 ? "Select conditions..." : undefined
            }
            data={CONDITION_OPTIONS}
            value={item.conditions}
            onChange={(value) => handleFieldChange("conditions", value)}
            searchable
            maxDropdownHeight={200}
          />
        </SimpleGrid>

        <Textarea
          label="Additional Preferences"
          placeholder="E.g. CBT preferred, female provider, within 10 mi..."
          value={item.preferences}
          onChange={(e) =>
            handleFieldChange("preferences", e.currentTarget.value)
          }
          autosize
          minRows={1}
          maxRows={3}
        />
      </Stack>
    </Paper>
  );
};
